import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { FilterValues } from "../../../data-model/filterValues";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { TableNames } from "../../../enums/tableNames";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import {
  buildWorklistFetchPayload,
  PharmaStpFileFilterPayload,
} from "../utils/buildWorklistFetchPayload";
import { CallingWorklistView } from "../utils/buildWorklistFilters";
import { CallingWorklistTableState } from "./useCallingWorklistTableData";
import { useFilterPayload } from "./useFilterPayload";

export interface UseNextPageDataFetchReturn {
  endIndex: number;
  setEndIndex: React.Dispatch<React.SetStateAction<number>>;
}

export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
  pharmaStpFileState: CallingWorklistTableState,
  setPharmaStpFileState: React.Dispatch<
    React.SetStateAction<CallingWorklistTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
  pageSize: number = 120,
  /** When true, pagination requests use the archive list payload. */
  isArchiveView: boolean = false,
  /** Worklist the payload is for; the Pending list also filters on response status. */
  view: CallingWorklistView = CallingWorklistView.NON_EPA,
  /** Table whose pagination bookkeeping this list owns. */
  tableName: TableNames = TableNames.CALLING_WORKLIST_ORDERS,
): UseNextPageDataFetchReturn => {
  const { setTableDataForContext } = useTablesContext();
  const [endIndex, setEndIndex] = useState<number>(0);

  const lastRequestedPageRef = useRef<number>(0);

  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    pageSize,
    isArchiveView,
    view,
  );

  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setPharmaStpFileStateRef = useRef(setPharmaStpFileState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setPharmaStpFileStateRef.current = setPharmaStpFileState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setPharmaStpFileState,
    setTableData,
    setTableDataForContext,
  ]);

  useEffect(() => {
    lastRequestedPageRef.current = 0;
  }, [validFilters, searchTerm, isArchiveView]);

  const shouldFetchMoreData = useMemo(() => {
    const currentPageRowCount = pharmaStpFileState.data?.row_count ?? 0;
    const currentPage = pharmaStpFileState.data?.page ?? 1;
    const totalPages = pharmaStpFileState.data?.total_pages ?? 1;
    const nextPageToFetch = currentPage + 1;

    const previousPagesRows = (currentPage - 1) * pageSize;
    const cumulativeRowsLoaded = previousPagesRows + currentPageRowCount;

    return (
      endIndex >= cumulativeRowsLoaded - 1 &&
      currentPage < totalPages &&
      !pharmaStpFileState.loading &&
      !pharmaStpFileState.showInlineLoader &&
      nextPageToFetch > lastRequestedPageRef.current
    );
  }, [
    endIndex,
    pharmaStpFileState.data?.row_count,
    pharmaStpFileState.data?.page,
    pharmaStpFileState.data?.total_pages,
    pharmaStpFileState.loading,
    pharmaStpFileState.showInlineLoader,
    pageSize,
  ]);

  const payloadFinal = useMemo<PharmaStpFileFilterPayload>(() => {
    if (filterPayload === null) {
      return buildWorklistFetchPayload({
        view,
        isArchiveView,
        validFilters,
        searchTerm,
        pageSize,
      });
    }
    return filterPayload;
  }, [filterPayload, validFilters, searchTerm, pageSize, isArchiveView, view]);

  const payloadFinalWithPage = useMemo<PharmaStpFileFilterPayload>(() => {
    return {
      ...payloadFinal,
      page: (pharmaStpFileState.data?.page ?? 0) + 1,
      request_id: uuidv4(),
    };
  }, [payloadFinal, pharmaStpFileState.data?.page]);

  const currentPage = pharmaStpFileState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    const nextPage = currentPage + 1;
    lastRequestedPageRef.current = nextPage;

    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage,
      setPharmaStpFileStateRef.current,
      setTableDataRef.current,
    );
    setTableDataForContextRef.current(tableName, {
      lastBatchFetched: nextPage,
    });
  }, [payloadFinalWithPage, currentPage, tableName]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
